import React from 'react';
import Seo from '../components/Seo';
import { Link } from 'react-router-dom';
import { Flame, ArrowRight } from 'lucide-react';
import { BRAND } from '../mock/mock';
import Reveal from '../components/Reveal';

const UPDATED = 'July 2025';

const Section = ({ n, title, children }) => (
  <Reveal className="mb-9">
    <h2 className="font-display text-2xl text-fire-cream mb-3">
      <span className="text-gradient-gold mr-2">{n}.</span>{title}
    </h2>
    <div className="text-fire-cream/70 font-body leading-[1.85] space-y-3">{children}</div>
  </Reveal>
);

const Terms = () => {
  return (
    <div className="bg-fire-ink">
      <Seo title="Terms & Conditions" path="/terms" description="The terms and conditions for using the After the Fire™ website, shop and services." />
      {/* Header */}
      <section className="pt-32 pb-12 ember-texture text-center">
        <div className="max-w-3xl mx-auto px-6">
          <Flame className="w-8 h-8 text-fire-gold mx-auto mb-5 animate-flicker" strokeWidth={1.4} />
          <span className="font-marcellus tracking-[0.25em] text-fire-gold text-xs">THE FINE PRINT</span>
          <h1 className="font-display text-4xl md:text-6xl text-fire-cream mt-4">Terms &amp; Conditions</h1>
          <p className="text-fire-cream/55 font-body text-sm mt-4">Last updated: {UPDATED}</p>
        </div>
      </section>

      <section className="pb-24">
        <div className="max-w-[820px] mx-auto px-5 lg:px-8">
          <Reveal className="card-dark rounded-2xl p-6 md:p-8 mb-10">
            <p className="text-fire-cream/70 font-body leading-relaxed">
              Welcome to After the Fire™. These Terms &amp; Conditions (“Terms”) govern your use of our website and the
              products, services, events and conversations offered by Dr. Mischell “Dr. Gee” Glenn and After the Fire™
              (“we,” “us,” “our”). By accessing this website or purchasing our products or services, you agree to these Terms.
              Please read them carefully.
            </p>
          </Reveal>

          <Section n="1" title="Acceptance of These Terms">
            <p>
              By using this website, booking a Fireside Clarity Call™, requesting a speaking engagement, purchasing merchandise
              or otherwise engaging with After the Fire™, you confirm that you are at least 18 years of age and agree to be
              bound by these Terms and our Privacy Policy. If you do not agree, please do not use the site or our services.
            </p>
          </Section>

          <Section n="2" title="Not Professional Advice">
            <p>
              After the Fire™ offers encouragement, ministry, motivational content, lived-experience mentorship and
              personal-development resources. Our content, events, books, music and Fireside Clarity Calls™ are
              <span className="text-fire-goldlight"> not psychotherapy, mental-health treatment, medical advice, legal advice or crisis services</span>,
              and are not a substitute for care from a licensed professional.
            </p>
            <p>
              If you are experiencing a crisis or a medical, psychological, legal or safety emergency, please contact emergency
              services or an appropriate licensed counselor, physician, attorney, advocate or crisis resource immediately. You can
              have faith and ask for help.
            </p>
          </Section>

          <Section n="3" title="Fireside Clarity Calls™ &amp; Bookings">
            <p>
              Fireside Clarity Calls™ are private, peer/lived-experience conversations. Scheduling is subject to availability.
              Call packages (such as multi-call bundles) are valid within the timeframe stated at purchase. Sessions are intended
              for the individual who booked them and may not be recorded or redistributed without written consent.
            </p>
            <p>
              Speaking, conference and event bookings are confirmed only upon a signed agreement and any required deposit.
              Travel and production costs, where applicable, are the responsibility of the host organization unless otherwise agreed.
            </p>
          </Section>

          <Section n="4" title="Purchases, Merchandise &amp; Payments">
            <p>
              Prices for merchandise, books, music and digital products are shown in U.S. dollars and may change without notice.
              We strive to describe products accurately; colors and details may vary slightly. Digital products (such as the
              soundtrack) are delivered electronically and are for personal, non-commercial use only.
            </p>
            <p>
              Please note: online ordering and checkout on this site are currently in preview. Any order flow shown is for
              demonstration and no payment is processed until our secure store is live.
            </p>
          </Section>

          <Section n="5" title="Refunds &amp; Cancellations">
            <p>
              Requests to reschedule a Fireside Clarity Call™ should be made at least 24 hours in advance. Digital downloads are
              generally non-refundable once accessed. Physical merchandise may be eligible for exchange or refund when returned
              unused within the period stated at purchase. Event deposits are handled per the individual booking agreement.
            </p>
          </Section>

          <Section n="6" title="Intellectual Property">
            <p>
              The After the Fire™ name, the R.E.F.I.N.E.™ framework, the Personal Fire Escape Plan™, the Green Room Fire
              Fighter™ concept, “Tested by the Fire. Refined by the Flame.™,” logos, written content, music, images and
              designs are the property of After the Fire™ and Dr. Gee, protected by applicable trademark and copyright laws.
              You may not copy, reproduce, resell or distribute our content without written permission.
            </p>
          </Section>

          <Section n="7" title="User Conduct">
            <p>
              We ask that all participants treat this community, its members and Dr. Gee with respect. You agree not to use our
              site or services for unlawful purposes, to harass others, or to share content that is abusive, defamatory or
              infringes the rights of others.
            </p>
          </Section>

          <Section n="8" title="Limitation of Liability">
            <p>
              To the fullest extent permitted by law, After the Fire™ and Dr. Gee are not liable for any indirect, incidental or
              consequential damages arising from your use of the website, products, events or conversations. Our resources are
              provided “as is” to support and encourage — the choices and outcomes in your life remain your own.
            </p>
          </Section>

          <Section n="9" title="Changes to These Terms">
            <p>
              We may update these Terms from time to time. Changes take effect when posted on this page with an updated date.
              Your continued use of the site after changes are posted constitutes acceptance of the revised Terms.
            </p>
          </Section>

          <Section n="10" title="Contact Us">
            <p>
              Questions about these Terms? Reach out any time at{' '}
              <a href={`mailto:${BRAND.email}`} className="text-fire-goldlight underline underline-offset-2 hover:text-fire-gold">{BRAND.email}</a>.
            </p>
          </Section>

          <Reveal className="mt-12 rounded-2xl p-8 text-center bg-gradient-to-br from-fire-maroon to-fire-maroondark border border-fire-gold/30">
            <p className="font-cormorant italic text-fire-cream text-xl">
              “There’s an exit. Come this way.”
            </p>
            <div className="flex flex-wrap gap-4 justify-center mt-6">
              <Link to="/privacy" className="btn-outline-gold rounded-full px-6 py-3 font-semibold text-sm">Read our Privacy Policy</Link>
              <Link to="/contact" className="btn-gold rounded-full px-6 py-3 font-semibold inline-flex items-center gap-2 text-sm">
                Contact Dr. Gee <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Terms;
